import type { FieldId } from '@eformulieren/model'
import * as Y from 'yjs'
import { COMMENT_MARK } from '../comments/CommentMark.js'
import { richtextFragment } from './sectionDocument.js'

/**
 * Het anker van een opmerking zetten zonder editor: voor een meelezer, door de pod (R-73, besluit 8,
 * keuze van 1 november 2026).
 *
 * Een opmerking hangt aan een stuk tekst, zoals in de CitatenPOC (eisenboek 12.9): een mark op de
 * tekst, met het draad-id erin (`CommentMark.ts`). Een schrijver zet die mark in zijn editor. Een
 * meelezer kan dat niet, want zijn verbinding neemt niets aan. Hij selecteert de tekst, en de pod zet
 * de mark voor hem op dat bereik, als één wijziging met het draadje erbij (`writeCommentAs` in de pod).
 *
 * **Het bereik reist als twee relatieve posities van Yjs** en niet als tekenposities: een
 * medeschrijver die intussen erboven typt verschuift een tekenpositie, terwijl een relatieve positie
 * aan het teken blijft hangen. Een positie die niet meer bestaat of buiten dit veld ligt, wordt
 * geweigerd in plaats van ergens anders neergezet.
 *
 * **Alleen een mark, nooit tekst.** De pod roept alleen `format` aan, en controleert daarna dat de
 * vorm van het fragment (elementen, hun attributen en de letters) gelijk is gebleven; zo niet, dan
 * gaat er niets uit. De vorm van de mark is die van y-tiptap voor een mark die zichzelf uitsluit: de
 * naam van de mark als sleutel, zijn attributen als waarde (`marksToAttributes`).
 *
 * **Geen tweede opmerking over een eerste heen.** De mark draagt één draad-id (`CommentMark.ts`); een
 * nieuw anker over een bestaand zou het andere draadje zijn anker afnemen.
 */

export type CommentRangeRefusal = 'bereik-onleesbaar' | 'bereik-onbekend' | 'bereik-buiten-veld' | 'bereik-leeg' | 'bereik-heeft-al-een-opmerking'

export class CommentRangeRefused extends Error {
  constructor(readonly code: CommentRangeRefusal) {
    super(code)
    this.name = 'CommentRangeRefused'
  }
}

/** Een bereik in een rich-text-veld, als twee gecodeerde relatieve posities van Yjs. */
export interface CommentRange {
  readonly van: string
  readonly tot: string
}

export function encodeRelativePosition(position: Y.RelativePosition): string {
  let binary = ''
  for (const byte of Y.encodeRelativePosition(position)) binary += String.fromCharCode(byte)
  return btoa(binary)
}

export function decodeRelativePosition(encoded: string): Y.RelativePosition {
  let bytes: Uint8Array
  try {
    bytes = Uint8Array.from(atob(encoded), (char) => char.charCodeAt(0))
    return Y.decodeRelativePosition(bytes)
  } catch {
    throw new CommentRangeRefused('bereik-onleesbaar')
  }
}

/** De stukken tekst van het fragment in documentvolgorde. */
function textsOf(fragment: Y.XmlFragment): Y.XmlText[] {
  const out: Y.XmlText[] = []
  const walk = (node: Y.XmlElement | Y.XmlFragment): void => {
    for (const child of node.toArray()) {
      if (child instanceof Y.XmlText) out.push(child)
      else if (child instanceof Y.XmlElement) walk(child)
    }
  }
  walk(fragment)
  return out
}

/** De vorm zonder opmaak: elementen, hun attributen en de letters. Een mark verandert dit niet. */
function shapeOf(node: Y.XmlFragment | Y.XmlElement | Y.XmlText): string {
  if (node instanceof Y.XmlText) {
    // Aaneengeregen: een mark splitst een stuk tekst in meer delen, en dat is geen andere tekst.
    return JSON.stringify((node.toDelta() as { insert: unknown }[]).map((op) => (typeof op.insert === 'string' ? op.insert : `\u0000${JSON.stringify(op.insert)}\u0000`)).join(''))
  }
  const children = node
    .toArray()
    .map((child) => (child instanceof Y.XmlText || child instanceof Y.XmlElement ? shapeOf(child) : '?'))
    .join(',')
  if (node instanceof Y.XmlElement) return `<${node.nodeName} ${JSON.stringify(node.getAttributes())}>[${children}]`
  return `[${children}]`
}

/** De letters van `text` tussen `from` en `to`, en of daar al een opmerking op staat. */
function slice(text: Y.XmlText, from: number, to: number): { readonly letters: string; readonly marked: boolean } {
  let offset = 0
  let letters = ''
  let marked = false
  for (const op of text.toDelta() as { insert: unknown; attributes?: Record<string, unknown> }[]) {
    const length = typeof op.insert === 'string' ? op.insert.length : 1
    const start = Math.max(from, offset)
    const end = Math.min(to, offset + length)
    if (start < end) {
      if (typeof op.insert === 'string') letters += op.insert.slice(start - offset, end - offset)
      if (op.attributes !== undefined && Object.keys(op.attributes).some((key) => key === COMMENT_MARK || key.startsWith(`${COMMENT_MARK}--`))) marked = true
    }
    offset += length
  }
  return { letters, marked }
}

function inside(type: Y.XmlText, fragment: Y.XmlFragment): boolean {
  let current: unknown = type
  while (current instanceof Y.AbstractType) {
    if (current === fragment) return true
    current = (current as { _item: { parent: unknown } | null })._item?.parent ?? null
  }
  return false
}

/**
 * Zet de mark van draadje `threadId` op het bereik in veld `fieldId`, in één transactie op `doc`.
 * Levert het citaat: de letters in het bereik, met een spatie tussen twee alinea's.
 */
export function markCommentRange(doc: Y.Doc, fieldId: FieldId, range: CommentRange, threadId: string): { readonly citaat: string } {
  const fragment = richtextFragment(doc, fieldId)
  const start = Y.createAbsolutePositionFromRelativePosition(decodeRelativePosition(range.van), doc)
  const end = Y.createAbsolutePositionFromRelativePosition(decodeRelativePosition(range.tot), doc)
  if (start === null || end === null || !(start.type instanceof Y.XmlText) || !(end.type instanceof Y.XmlText)) {
    throw new CommentRangeRefused('bereik-onbekend')
  }
  if (!inside(start.type, fragment) || !inside(end.type, fragment)) throw new CommentRangeRefused('bereik-buiten-veld')
  const texts = textsOf(fragment)
  const first = texts.indexOf(start.type)
  const last = texts.indexOf(end.type)
  if (first === -1 || last === -1) throw new CommentRangeRefused('bereik-buiten-veld')
  if (first > last || (first === last && start.index >= end.index)) throw new CommentRangeRefused('bereik-leeg')

  const pieces = texts.slice(first, last + 1).flatMap((text, place) => {
    const from = place === 0 ? start.index : 0
    const to = first + place === last ? end.index : text.length
    return to > from ? [{ text, from, to, ...slice(text, from, to) }] : []
  })
  if (pieces.length === 0) throw new CommentRangeRefused('bereik-leeg')
  if (pieces.some((piece) => piece.marked)) throw new CommentRangeRefused('bereik-heeft-al-een-opmerking')

  const before = shapeOf(fragment)
  doc.transact(() => {
    for (const piece of pieces) piece.text.format(piece.from, piece.to - piece.from, { [COMMENT_MARK]: { draadId: threadId } })
  })
  // Een vangnet en geen controle die in de praktijk afgaat: `format` verandert geen letters. Gaat het
  // toch mis, dan breekt dit af voordat de wijziging uit dit document wordt doorgegeven.
  if (shapeOf(fragment) !== before) throw new Error('het anker van een opmerking veranderde de tekst; niets doorgegeven')
  return { citaat: pieces.map((piece) => piece.letters).join(' ') }
}

/**
 * Haalt de mark van draadje `threadId` uit veld `fieldId`, zonder editor (eisenboek 12.45).
 *
 * Voor een draadje dat er na een verwijdering niet meer is: anders blijft de tekst gemarkeerd met een
 * draadje dat niemand ziet, en weigert {@link markCommentRange} een nieuwe opmerking op die tekst
 * (`bereik-heeft-al-een-opmerking`). Alleen de mark met dit draad-id, met dezelfde sleutelvormen als
 * `slice` herkent; een ander anker in dezelfde tekst blijft staan. Alleen opmaak, nooit tekst, met
 * hetzelfde vangnet als bij het zetten.
 *
 * Levert of er iets is weggehaald. Zelf geen transactie: de aanroeper zet het in de zijne, samen met
 * het verwijderen van het bericht.
 */
export function unmarkCommentThread(doc: Y.Doc, fieldId: FieldId, threadId: string): boolean {
  const fragment = richtextFragment(doc, fieldId)
  const before = shapeOf(fragment)
  let touched = false
  for (const text of textsOf(fragment)) {
    let offset = 0
    const spans: { readonly from: number; readonly length: number; readonly key: string }[] = []
    for (const op of text.toDelta() as { insert: unknown; attributes?: Record<string, unknown> }[]) {
      const length = typeof op.insert === 'string' ? op.insert.length : 1
      for (const [key, value] of Object.entries(op.attributes ?? {})) {
        if (key !== COMMENT_MARK && !key.startsWith(`${COMMENT_MARK}--`)) continue
        if (typeof value === 'object' && value !== null && (value as { draadId?: unknown }).draadId === threadId) spans.push({ from: offset, length, key })
      }
      offset += length
    }
    // `format` verandert geen lengtes, dus de plekken uit de doorloop blijven geldig.
    for (const span of spans) text.format(span.from, span.length, { [span.key]: null })
    if (spans.length > 0) touched = true
  }
  if (shapeOf(fragment) !== before) throw new Error('het anker weghalen veranderde de tekst; niets doorgegeven')
  return touched
}
